import { createReducer, on } from '@ngrx/store';
import * as PolicyActions from './policy.actions';
import { PolicySchemaResult } from '../models/policy-schema.model';
import { CreatePolicyForm } from '../models/policy-form.model';
import {
  PolicyListItem,
  PolicyFilterStatus,
  PolicyOrderBy,
  PolicyOrder,
} from '../models/policy-list.model';

/**
 * Policy State Interface
 */
export interface PolicyState {
  // Schema state
  schema: PolicySchemaResult | null;
  schemaLoading: boolean;
  schemaError: string | null;

  // Users state
  users: any | null;
  usersLoading: boolean;
  usersError: string | null;

  // Form state
  form: CreatePolicyForm;
  formDirty: boolean;

  // Submit state
  submitting: boolean;
  submitError: string | null;

  // Policy List state
  policies: PolicyListItem[];
  listLoading: boolean;
  listError: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: {
    status: PolicyFilterStatus;
    domain: string | null;
  };
  sort: {
    sortBy: PolicyOrderBy;
    sortType: PolicyOrder;
  };
}

/**
 * Initial State
 */
export const initialState: PolicyState = {
  // Schema
  schema: null,
  schemaLoading: false,
  schemaError: null,

  // Users
  users: null,
  usersLoading: false,
  usersError: null,

  // Form
  form: {
    domain: null,
    resource: null,
    action: null,
    scope: null,
  },
  formDirty: false,

  // Submit
  submitting: false,
  submitError: null,

  // Policy List
  policies: [],
  listLoading: false,
  listError: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    status: PolicyFilterStatus.ALL,
    domain: null,
  },
  sort: {
    sortBy: PolicyOrderBy.CREATED_AT,
    sortType: 'DESC' as PolicyOrder,
  },
};

/**
 * Policy Reducer
 */
export const policyReducer = createReducer(
  initialState,

  // Load Policy Schema
  on(PolicyActions.loadPolicySchema, state => ({
    ...state,
    schemaLoading: true,
    schemaError: null,
  })),

  on(PolicyActions.loadPolicySchemaSuccess, (state, { schema }) => {
    return {
      ...state,
      schema,
      schemaLoading: false,
      schemaError: null,
    };
  }),

  on(PolicyActions.loadPolicySchemaFailure, (state, { error }) => ({
    ...state,
    schemaLoading: false,
    schemaError: error,
  })),

  // Update Form Fields
  on(PolicyActions.updateDomain, (state, { domain }) => ({
    ...state,
    form: {
      ...state.form,
      domain,
      // Reset dependent fields when domain changes
      resource: null,
      action: null,
      scope: null,
    },
    formDirty: true,
  })),

  on(PolicyActions.updateResource, (state, { resource }) => ({
    ...state,
    form: {
      ...state.form,
      resource,
      // Reset dependent fields when resource changes
      action: null,
      scope: null,
    },
    formDirty: true,
  })),

  on(PolicyActions.updateAction, (state, { action }) => ({
    ...state,
    form: {
      ...state.form,
      action,
    },
    formDirty: true,
  })),

  on(PolicyActions.updateScope, (state, { scope }) => ({
    ...state,
    form: {
      ...state.form,
      scope,
    },
    formDirty: true,
  })),

  // Reset Form
  on(PolicyActions.resetForm, state => ({
    ...state,
    form: initialState.form,
    formDirty: false,
    submitError: null,
  })),

  // Submit Policy
  on(PolicyActions.submitPolicy, state => ({
    ...state,
    submitting: true,
    submitError: null,
  })),

  on(PolicyActions.submitPolicySuccess, state => ({
    ...state,
    submitting: false,
    submitError: null,
    form: initialState.form,
    formDirty: false,
  })),

  on(PolicyActions.submitPolicyFailure, (state, { error }) => ({
    ...state,
    submitting: false,
    submitError: error,
  })),

  // Load Users
  on(PolicyActions.loadUsers, state => ({
    ...state,
    usersLoading: true,
    usersError: null,
  })),

  on(PolicyActions.loadUsersSuccess, (state, { users }) => {
    return {
      ...state,
      users,
      usersLoading: false,
      usersError: null,
    };
  }),

  on(PolicyActions.loadUsersFailure, (state, { error }) => ({
    ...state,
    usersLoading: false,
    usersError: error,
  })),

  // Policy List Actions
  on(PolicyActions.loadPolicies, state => ({
    ...state,
    listLoading: true,
    listError: null,
  })),
  on(PolicyActions.loadPoliciesSuccess, (state, { policies, pagination }) => ({
    ...state,
    policies,
    pagination,
    listLoading: false,
    listError: null,
  })),
  on(PolicyActions.loadPoliciesFailure, (state, { error }) => ({
    ...state,
    listLoading: false,
    listError: error,
  })),
  on(PolicyActions.setPage, (state, { page }) => ({
    ...state,
    pagination: {
      ...state.pagination,
      page,
    },
  })),
  on(PolicyActions.setPageSize, (state, { pageSize }) => ({
    ...state,
    pagination: {
      ...state.pagination,
      limit: pageSize,
      page: 1, // Reset to first page when changing page size
    },
  })),
  on(PolicyActions.setStatusFilter, (state, { status }) => ({
    ...state,
    filters: {
      ...state.filters,
      status,
    },
    pagination: {
      ...state.pagination,
      page: 1, // Reset to first page when filtering
    },
  })),
  on(PolicyActions.setDomainFilter, (state, { domain }) => ({
    ...state,
    filters: {
      ...state.filters,
      domain,
    },
    pagination: {
      ...state.pagination,
      page: 1, // Reset to first page when filtering
    },
  })),
  on(PolicyActions.setSort, (state, { sortBy, sortType }) => ({
    ...state,
    sort: {
      sortBy,
      sortType,
    },
  })),
  on(PolicyActions.resetFilters, state => ({
    ...state,
    filters: {
      status: PolicyFilterStatus.ALL,
      domain: null,
    },
    pagination: {
      ...state.pagination,
      page: 1,
    },
  }))
);
